import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { MapPin, Phone } from '@phosphor-icons/react';

const ContactInYourCountry = () => {
  const [countries, setCountries] = useState([]);
  const [churches, setChurches] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchCountries();
    else {
      setSelectedCountry('');
      setChurches([]);
    }
  }, [open]);

  useEffect(() => {
    if (selectedCountry) fetchChurches(selectedCountry);
    else setChurches([]);
  }, [selectedCountry]);

  const fetchCountries = async () => {
    const { data } = await supabase
      .from('countries')
      .select('*')
      .eq('is_active', true)
      .order('name');
    setCountries(data || []);
  };

  const fetchChurches = async (code) => {
    setLoading(true);
    const { data } = await supabase
      .from('churches')
      .select('*')
      .eq('country_code', code)
      .order('name');
    setChurches(data || []);
    setLoading(false);
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#F04A22] hover:bg-[#D03A18] text-white px-6 py-3 rounded-md font-semibold">
          Contacto en tu País
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black">Contacto en tu País</DialogTitle>
          <p className="text-[#737373] mt-2">Encuentra una iglesia cerca de ti</p>
        </DialogHeader>
        <div className="mt-6 space-y-6">
          <div>
            <label className="text-sm font-bold uppercase tracking-widest mb-3 block">Selecciona tu País</label>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Elige un país..." />
              </SelectTrigger>
              <SelectContent>
                {countries.map(c => (
                  <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading && (
            <div className="text-center py-8 text-[#737373]">
              <p>Cargando iglesias...</p>
            </div>
          )}

          {!loading && selectedCountry && churches.length === 0 && (
            <div className="text-center py-8 text-[#737373] bg-[#FAFAFA] rounded-lg border border-[#E5E5E5]">
              <p>No hay iglesias disponibles en este país aún.</p>
            </div>
          )}

          {!loading && churches.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold">Iglesias disponibles:</h3>
              {churches.map((church) => (
                <div key={church.id} className="bg-white border border-[#E5E5E5] rounded-lg p-6 hover:shadow-[0_8px_32px_rgba(240,74,34,0.1)] transition-shadow">
                  <h4 className="font-bold text-xl mb-4 text-[#F04A22]">{church.name}</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone size={20} weight="bold" className="text-[#F04A22] flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-[#737373]">Teléfono</p>
                        <p className="font-semibold">{church.phone}</p>
                      </div>
                      <Button onClick={() => handleCall(church.phone)} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white">
                        Llamar
                      </Button>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin size={20} weight="bold" className="text-[#F04A22] flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm text-[#737373]">Dirección</p>
                        {church.city && <p className="font-semibold">{church.city}</p>}
                        <p className="text-[#111111]/80">{church.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!selectedCountry && countries.length === 0 && (
            <div className="text-center py-8 text-[#737373]">
              <p>No hay países disponibles.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactInYourCountry;
